import { Client } from '@twilio/conversations';
import {
  createContext,
  Dispatch,
  FC,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { update } from 'services/utilsService';
import { getTwilioAccessTokenAction } from 'state/actions/twilioActions';
import {
  CLEAR_TWILIO_CONTEXT,
  GET_TWILIO_ACCESS_TOKEN_FAILURE,
  GET_TWILIO_ACCESS_TOKEN_REQUEST,
  GET_TWILIO_ACCESS_TOKEN_SUCCESS,
} from 'state/actionTypes/twilioActionTypes';
import { useStorageRequestContext } from '../storageRequestContext/storageRequest.context';
import { useUserContext } from '../userContext/userContext';
import { TwilioAction, TwilioReducer, TwilioState } from './TwilioContext.types';

type TwilioContext = {
  twilioState: TwilioState;
  dispatch: Dispatch<TwilioAction>;
  client: Client | null;
};
type UseTwilioContext = () => TwilioContext;

const TwilioContext = createContext({} as TwilioContext);

const INITIAL_STATE: TwilioState = {
  gettingTwilioAccessToken: { status: 'Initial' },
  twilioAccessToken: null,
};

const twilioReducer: TwilioReducer = (state, action) => {
  const { type } = action;

  switch (type) {
    case GET_TWILIO_ACCESS_TOKEN_REQUEST:
      return update(state, {
        gettingTwilioAccessToken: { status: 'InProgress' },
      });

    case GET_TWILIO_ACCESS_TOKEN_SUCCESS:
      return update(state, {
        gettingTwilioAccessToken: { status: 'Success' },
        twilioAccessToken: action.payload,
      });

    case GET_TWILIO_ACCESS_TOKEN_FAILURE:
      return update(state, {
        gettingTwilioAccessToken: { status: 'Error', error: action.payload },
      });

    case CLEAR_TWILIO_CONTEXT:
      return INITIAL_STATE;

    default:
      return state;
  }
};

export const TwilioProvider: FC = ({ children }) => {
  const [twilioState, dispatch] = useReducer(twilioReducer, INITIAL_STATE);
  const [client, setClient] = useState<Client | null>(null);
  const {
    user: { userData },
  } = useUserContext();

  const {
    storageRequestsState: { storageRequests },
  } = useStorageRequestContext();

  useEffect(() => {
    if (userData && twilioState.gettingTwilioAccessToken.status === 'Initial') {
      getTwilioAccessTokenAction(dispatch);
    }
  }, [twilioState.gettingTwilioAccessToken.status, userData]);

  useEffect(() => {
    const atLeastOneStorageRequestAccepted = storageRequests.some(
      (storageRequest) => storageRequest.requestStatus === 'accepted',
    );
    if (twilioState.twilioAccessToken && atLeastOneStorageRequestAccepted) {
      const client = new Client(twilioState.twilioAccessToken);

      client.on('initialized', () => {
        setClient(client);
      });

      client.on('initFailed', ({ error }) => {
        if (error) {
          dispatch({ type: GET_TWILIO_ACCESS_TOKEN_FAILURE, payload: error.message });
        }
      });

      client.on('tokenAboutToExpire', () => {
        getTwilioAccessTokenAction(dispatch);
      });
    }
  }, [storageRequests, twilioState.twilioAccessToken]);

  useEffect(() => () => dispatch({ type: CLEAR_TWILIO_CONTEXT }), [dispatch]);

  return (
    <TwilioContext.Provider value={{ twilioState, dispatch, client }}>
      {children}
    </TwilioContext.Provider>
  );
};

export const useTwilioContext: UseTwilioContext = () => useContext(TwilioContext);
